import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { AnimeGrid } from '@/components/AnimeGrid'
import { LoadingSpinner } from '@/components/LoadingSpinner'

export default function GenrePage() {
  const { slug } = useParams<{ slug: string }>()
  const [animeList, setAnimeList] = useState<Anime[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  const genre = slug as string
  const genreTitle = genre.charAt(0).toUpperCase() + genre.slice(1).replace('-', ' ')

  useEffect(() => {
    fetchAnime()
  }, [genre])

  const fetchAnime = async (pageNum = 1) => {
    try {
      setLoading(pageNum === 1)
      
      const results = await anilistService.getAnimeByGenre(genreTitle, 20, pageNum)
      
      if (pageNum === 1) {
        setAnimeList(results)
      } else {
        setAnimeList(prev => [...prev, ...results])
      }
      
      setHasNextPage(results.length === 20)
      setPage(pageNum)
    } catch (error) {
      console.error('Error fetching anime:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMore = () => {
    if (hasNextPage && !loading) {
      fetchAnime(page + 1)
    }
  }

  return (
    <div className="min-h-screen bg-dark-400 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            {genreTitle} Anime
          </h1>
          <p className="text-gray-400 text-lg">
            Discover the best {genreTitle.toLowerCase()} anime series and movies
          </p>
        </div>

        {/* Content */}
        {loading && animeList.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : animeList.length > 0 ? (
          <>
            <div className="mb-6">
              <p className="text-gray-400">
                {animeList.length} {genreTitle.toLowerCase()} anime found
              </p>
            </div>

            <AnimeGrid animeList={animeList} />

            {/* Load More */}
            {hasNextPage && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              No {genreTitle.toLowerCase()} anime found
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
